import { nevisignFetch } from "@nevisign/reader-core";
import type { ContractWorkflowStateDTO } from "@platform/shared-types";
import { ensureFreshAccessToken } from "./platform-auth";

export type ContractIntakeBody = {
  title?: string;
  sourceText?: string;
  legalRequestId?: string;
  fileTexts?: { name: string; content: string; mime?: string }[];
  brief?: { contractType?: string };
};

export type ContractDetailLite = {
  id: string;
  title: string;
  status?: string;
  progressMessage?: string | null;
};

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  const raw = await res.text().catch(() => "");
  const trimmed = raw.trim();
  if (
    trimmed.startsWith("<!DOCTYPE") ||
    trimmed.startsWith("<html") ||
    /Upstream Error/i.test(trimmed)
  ) {
    return `${fallback} — پاسخ غیرJSON از CDN/پروکسی (HTTP ${res.status}). دوباره وارد شوید یا چند لحظه بعد تلاش کنید.`;
  }
  try {
    const err = JSON.parse(trimmed) as {
      error?: string | { message?: string };
      message?: string;
    };
    if (typeof err?.message === "string" && err.message.trim()) return err.message;
    if (typeof err?.error === "string" && err.error.trim()) return err.error;
    if (
      err?.error &&
      typeof err.error === "object" &&
      typeof err.error.message === "string"
    ) {
      return err.error.message;
    }
  } catch {
    /* ignore */
  }
  if (trimmed && trimmed.length < 280) return `${fallback}: ${trimmed}`;
  return `${fallback} (HTTP ${res.status})`;
}

/** POST /api/v1/contracts/from-intake — same as apps/web AI create. */
export async function createContractFromIntake(
  apiBase: string,
  body: ContractIntakeBody,
): Promise<ContractDetailLite> {
  await ensureFreshAccessToken();
  const res = await nevisignFetch(apiBase, "/contracts/from-intake", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, "خطا در ایجاد قرارداد با هوش مصنوعی"));
  }
  const data = (await res.json()) as { contract: ContractDetailLite };
  if (!data?.contract?.id) throw new Error("سرور شناسه قرارداد برنگرداند.");
  return data.contract;
}

/** POST /api/v1/contracts/blank */
export async function createBlankContract(
  apiBase: string,
  body?: { title?: string; category?: string },
): Promise<ContractDetailLite> {
  await ensureFreshAccessToken();
  const res = await nevisignFetch(apiBase, "/contracts/blank", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, "خطا در ایجاد سند خالی"));
  }
  const data = (await res.json()) as { contract: ContractDetailLite };
  if (!data?.contract?.id) throw new Error("سرور شناسه قرارداد برنگرداند.");
  return data.contract;
}

/** POST /api/v1/contracts/from-upload */
export async function createContractFromUpload(
  apiBase: string,
  file: File,
  opts?: { category?: string },
): Promise<ContractDetailLite> {
  await ensureFreshAccessToken();
  const form = new FormData();
  form.append("file", file);
  if (opts?.category?.trim()) form.append("category", opts.category.trim());
  const res = await nevisignFetch(apiBase, "/contracts/from-upload", {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, "خطا در بارگذاری سند"));
  }
  const data = (await res.json()) as { contract: ContractDetailLite };
  if (!data?.contract?.id) throw new Error("سرور شناسه قرارداد برنگرداند.");
  return data.contract;
}

export type StartContractSignatureBody = {
  coverSheetEnabled?: boolean;
  forceReauthAtSign?: boolean;
  deadlineAt?: string | null;
  mode?: "in_platform" | "gateway" | "samt_root_cert";
  redirectUrl?: string | null;
  failureRedirectUrl?: string | null;
  skipSms?: boolean;
  signers: Array<{
    userId?: string;
    phone?: string;
    displayName?: string;
    partyName?: string;
    signatureType?: "in_platform" | "gateway" | "samt_root_cert" | string;
    identityMethod?: string;
    order?: number;
  }>;
};

/** POST /api/v1/contracts/:id/signatures */
export async function startContractSignature(
  apiBase: string,
  contractId: string,
  body: StartContractSignatureBody,
): Promise<ContractWorkflowStateDTO> {
  await ensureFreshAccessToken();
  const res = await nevisignFetch(
    apiBase,
    `/contracts/${encodeURIComponent(contractId)}/signatures`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, "خطا در شروع فرایند امضا"));
  }
  const data = (await res.json()) as { workflow?: ContractWorkflowStateDTO };
  if (!data.workflow) throw new Error("پاسخ امضا از سرور ناقص بود.");
  return data.workflow;
}

export type ApprovalActAction =
  | "seen"
  | "approve"
  | "reject"
  | "needs_revision"
  | "comment";

/** POST /api/v1/contracts/:id/approvals/:processId/act */
export async function actOnApproval(
  apiBase: string,
  contractId: string,
  processId: string,
  body: {
    action: ApprovalActAction;
    comment?: string;
    rejectionReason?: string;
    revisionReason?: string;
  },
): Promise<void> {
  await ensureFreshAccessToken();
  const res = await nevisignFetch(
    apiBase,
    `/contracts/${contractId}/approvals/${processId}/act`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, "خطا در ثبت وضعیت تأیید"));
  }
}

function htmlToPlain(html: string): string {
  if (typeof document === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.innerText || tmp.textContent || "").replace(/\s+\n/g, "\n").trim();
}

/** Best-effort extract attachment text for AI intake (client-side). */
export async function readFileAsTextAttachment(
  file: File,
): Promise<{ name: string; content: string; mime?: string }> {
  const mime = file.type || undefined;
  const name = file.name || "file";
  const lower = name.toLowerCase();

  if (
    mime?.startsWith("text/") ||
    /\.(txt|md|markdown|csv|json|html?|xml)$/i.test(name)
  ) {
    const content = await file.text();
    return { name, content: content.slice(0, 80_000), mime };
  }

  try {
    const buffer = await file.arrayBuffer();

    if (lower.endsWith(".pdf") || mime === "application/pdf") {
      const { extractPdfPlainText } = await import("./pdfTextExtract");
      const content = await extractPdfPlainText(buffer);
      if (content.trim()) {
        return { name, content: content.slice(0, 80_000), mime };
      }
    }

    if (
      lower.endsWith(".docx") ||
      mime ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      const { renderDocxToHtml } = await import("@nevisign/reader-core");
      const rendered = await renderDocxToHtml(buffer);
      const content = htmlToPlain(rendered.html);
      if (content.trim()) {
        return { name, content: content.slice(0, 80_000), mime };
      }
    }
  } catch {
    /* fall through to placeholder */
  }

  return {
    name,
    content: `[فایل پیوست: ${name} — استخراج متن در کلاینت ممکن نشد؛ پس از ورود در سرور پردازش می‌شود.]`,
    mime,
  };
}
